interface CityData {
  city: string
  cityAscii: string
  latitude: number
  longitude: number
  country: string
  iso2: string
  iso3: string
  population: number
}

let citiesCache: Map<string, CityData> | null = null
let loadPromise: Promise<void> | null = null

async function loadCitiesFromCSV(): Promise<void> {
  if (citiesCache) return
  
  try {
    const response = await fetch('/assets/worldcities.csv')
    const text = await response.text()
    const lines = text.split('\n').slice(1) // Skip header
    
    const cities = new Map<string, CityData>()
    
    lines.forEach(line => {
      if (!line.trim()) return
      
      // Parse CSV line handling quoted values
      const regex = /(?:,|\n|^)("(?:(?:"")*[^"]*)*"|[^",\n]*|(?:\n|$))/g
      const parts: string[] = []
      let match
      
      while ((match = regex.exec(line)) !== null) {
        let value = match[1]
        // Remove quotes and unescape double quotes
        if (value.startsWith('"') && value.endsWith('"')) {
          value = value.substring(1, value.length - 1).replace(/""/g, '"')
        }
        parts.push(value)
      }
      
      // Filter out empty parts
      const cleanParts = parts.filter(p => p !== '')
      
      if (cleanParts.length < 8) return
      
      const city = cleanParts[0].trim()
      const cityAscii = cleanParts[1].trim()
      const latitude = parseFloat(cleanParts[2])
      const longitude = parseFloat(cleanParts[3])
      const country = cleanParts[4].trim()
      const iso2 = cleanParts[5].trim()
      const iso3 = cleanParts[6].trim()
      const population = parseInt(cleanParts[7]) || 0
      
      if (city && !isNaN(latitude) && !isNaN(longitude)) {
        cities.set(city, {
          city,
          cityAscii,
          latitude,
          longitude,
          country,
          iso2,
          iso3,
          population
        })
      }
    })
    
    citiesCache = cities
    console.log('[WorldCities] Loaded', cities.size, 'cities from CSV')
  } catch (error) {
    console.error('[WorldCities] Error loading cities CSV:', error)
    citiesCache = new Map()
  }
}

function ensureCitiesLoaded(): Promise<void> {
  if (!loadPromise) {
    loadPromise = loadCitiesFromCSV()
  }
  return loadPromise
}

export function getCityData(cityName: string | undefined): CityData | null {
  if (!cityName || !citiesCache) return null
  return citiesCache.get(cityName) || null
}

export function getCityCoordinates(cityName: string | undefined): { latitude: number; longitude: number } | null {
  const cityData = getCityData(cityName)
  if (!cityData) return null
  return {
    latitude: cityData.latitude,
    longitude: cityData.longitude
  }
}

export function getCityPopulation(cityName: string | undefined): number | null {
  const cityData = getCityData(cityName)
  return cityData?.population || null
}

export async function loadWorldCities(): Promise<void> {
  await ensureCitiesLoaded()
}

export function isWorldCitiesLoaded(): boolean {
  return citiesCache !== null
}

export function getAllCities(): CityData[] {
  if (!citiesCache) return []
  return Array.from(citiesCache.values())
}

// Preload cities on module import
if (typeof window !== 'undefined') {
  ensureCitiesLoaded()
}

